import {
  ALLIANCE_ROLES,
  RELATION_LABELS,
  RELATION_STATUS,
  ROLE_LABELS,
  type AllianceRole,
  type RelationStatus,
} from '@bienenkampf/shared';
import { Badge, Button, Card, Input } from '@bienenkampf/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { useState } from 'react';
import { CircularForm, PlannerCard } from '../components/PlannerCard.js';
import { useNow } from '../hooks/useNow.js';
import { formatAbsRel, formatNumber } from '../lib/time.js';
import { useTRPC } from '../lib/trpc.js';

const TONE: Record<RelationStatus, 'green' | 'neutral' | 'red'> = {
  ALLIED: 'green',
  NEUTRAL: 'neutral',
  HOSTILE: 'red',
};

export function AlliancePage() {
  const { worldId: worldIdStr } = useParams({ from: '/w/$worldId' });
  const worldId = Number(worldIdStr);
  const trpc = useTRPC();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const now = useNow(30_000);
  const ov = useQuery({
    ...trpc.alliance.overview.queryOptions({ worldId }),
    refetchInterval: 60_000,
  });
  const me = useQuery(trpc.auth.me.queryOptions());
  const myPlayerId = me.data?.players.find((p) => p.worldId === worldId)?.id;
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: trpc.alliance.pathKey() });
    void qc.invalidateQueries({ queryKey: trpc.chat.pathKey() });
    void qc.invalidateQueries({ queryKey: trpc.map.pathKey() });
  };
  const opts = { onSuccess: refresh };
  const create = useMutation(trpc.alliance.create.mutationOptions(opts));
  const apply = useMutation(trpc.alliance.applyFor.mutationOptions(opts));
  const withdraw = useMutation(trpc.alliance.withdraw.mutationOptions(opts));
  const decide = useMutation(trpc.alliance.decide.mutationOptions(opts));
  const setRole = useMutation(trpc.alliance.setRole.mutationOptions(opts));
  const kick = useMutation(trpc.alliance.kick.mutationOptions(opts));
  const leave = useMutation(trpc.alliance.leave.mutationOptions(opts));
  const updateDesc = useMutation(trpc.alliance.updateDescription.mutationOptions(opts));
  const setStance = useMutation(trpc.alliance.setStance.mutationOptions(opts));
  const openDm = useMutation(
    trpc.chat.openDm.mutationOptions({
      onSuccess: (r) =>
        void navigate({
          to: '/w/$worldId/chat',
          params: { worldId: worldIdStr },
          search: { c: r.conversationId },
        }),
    }),
  );
  const [name, setName] = useState('');
  const [tag, setTag] = useState('');
  const [desc, setDesc] = useState<string | null>(null);
  const err = [
    create,
    apply,
    withdraw,
    decide,
    setRole,
    kick,
    leave,
    updateDesc,
    setStance,
    openDm,
  ].find((m) => m.error)?.error;

  if (ov.isPending) return <p className="text-stone-400">Lade Stand…</p>;
  if (ov.isError) return <p className="text-red-400">{ov.error.message}</p>;
  const { membership, mine, all } = ov.data;
  const role = membership?.role;
  const isLeader = role === 'LEADER';
  const isCouncil = role === 'LEADER' || role === 'COUNCIL';
  const isDiplomat = isCouncil || role === 'DIPLOMAT';

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Stand</h1>
        <Link
          to="/w/$worldId/chat"
          params={{ worldId: worldIdStr }}
          className="text-sm text-amber-400"
        >
          ⇠ Chat
        </Link>
      </div>
      {err && <p className="text-sm text-red-400">{err.message}</p>}

      {!membership && (
        <Card title="Eigenen Stand gründen">
          <form
            className="flex flex-col gap-2 sm:flex-row sm:items-end"
            onSubmit={(e) => {
              e.preventDefault();
              create.mutate({ worldId, name, tag });
            }}
          >
            <div className="flex-1">
              <Input
                name="a-name"
                label="Name"
                minLength={3}
                maxLength={30}
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div className="w-32">
              <Input
                name="a-tag"
                label="Kürzel"
                minLength={2}
                maxLength={5}
                required
                value={tag}
                onChange={(e) => setTag(e.target.value)}
              />
            </div>
            <Button type="submit" loading={create.isPending}>
              Gründen
            </Button>
          </form>
        </Card>
      )}

      {membership?.role === 'APPLICANT' && (
        <Card title="Bewerbung offen">
          <p className="text-sm text-stone-300">
            Deine Bewerbung bei{' '}
            {all.find((a) => a.id === membership.allianceId)?.name ?? 'einem Stand'} wartet auf den
            Rat.
          </p>
          <Button
            className="mt-2"
            variant="ghost"
            size="sm"
            onClick={() => withdraw.mutate({ worldId })}
            loading={withdraw.isPending}
          >
            Zurückziehen
          </Button>
        </Card>
      )}

      {mine && role !== 'APPLICANT' && (
        <>
          <Card
            title={
              <span>
                [{mine.tag}] {mine.name}
              </span>
            }
            action={<Badge tone="amber">{ROLE_LABELS[role!]}</Badge>}
          >
            <div className="text-sm text-stone-400">
              {mine.members.length} Mitglieder · {formatNumber(mine.points)} Punkte
            </div>
            {desc === null ? (
              <p className="mt-2 whitespace-pre-wrap text-sm text-stone-200">
                {mine.description || <span className="text-stone-500">Keine Beschreibung.</span>}
              </p>
            ) : (
              <textarea
                className="mt-2 w-full rounded-lg border border-stone-700 bg-stone-900 p-2 text-sm"
                rows={4}
                maxLength={2000}
                value={desc}
                onChange={(e) => setDesc(e.target.value)}
              />
            )}
            <div className="mt-2 flex flex-wrap gap-2">
              {isCouncil &&
                (desc === null ? (
                  <Button size="sm" variant="ghost" onClick={() => setDesc(mine.description)}>
                    Beschreibung bearbeiten
                  </Button>
                ) : (
                  <>
                    <Button
                      size="sm"
                      onClick={() =>
                        updateDesc.mutate(
                          { worldId, description: desc },
                          { onSuccess: () => setDesc(null) },
                        )
                      }
                      loading={updateDesc.isPending}
                    >
                      Speichern
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setDesc(null)}>
                      Abbrechen
                    </Button>
                  </>
                ))}
              <Button
                size="sm"
                variant="danger"
                onClick={() => {
                  if (
                    window.confirm(
                      isLeader && mine.members.length === 1
                        ? 'Der Stand wird aufgelöst. Sicher?'
                        : 'Stand verlassen?',
                    )
                  )
                    leave.mutate({ worldId });
                }}
                loading={leave.isPending}
              >
                Verlassen
              </Button>
            </div>
          </Card>

          <Card title={`Mitglieder (${mine.members.length})`}>
            <ul className="divide-y divide-stone-800 text-sm">
              {mine.members.map((m) => (
                <li key={m.playerId} className="flex flex-wrap items-center gap-2 py-2">
                  <span className="min-w-0 flex-1">
                    <span className="font-medium">{m.name}</span>
                    <span className="ml-2 text-xs text-stone-400">
                      {formatNumber(m.points)} Punkte
                    </span>
                    {m.lastSeenAt && (
                      <span className="ml-2 text-xs text-stone-500">
                        zuletzt {formatAbsRel(m.lastSeenAt, now)}
                      </span>
                    )}
                  </span>
                  {isLeader && m.playerId !== myPlayerId ? (
                    <select
                      className="rounded-md border border-stone-700 bg-stone-900 px-2 py-1 text-xs"
                      value={m.role}
                      onChange={(e) =>
                        setRole.mutate({
                          worldId,
                          targetId: m.playerId,
                          role: e.target.value as AllianceRole,
                        })
                      }
                    >
                      {ALLIANCE_ROLES.filter((r) => r !== 'APPLICANT').map((r) => (
                        <option key={r} value={r}>
                          {ROLE_LABELS[r]}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <Badge>{ROLE_LABELS[m.role]}</Badge>
                  )}
                  {m.playerId !== myPlayerId && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => openDm.mutate({ worldId, otherPlayerId: m.playerId })}
                    >
                      ✉
                    </Button>
                  )}
                  {isCouncil && m.playerId !== myPlayerId && m.role !== 'LEADER' && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() =>
                        window.confirm(`${m.name} entfernen?`) &&
                        kick.mutate({ worldId, targetId: m.playerId })
                      }
                    >
                      ✕
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          </Card>

          {isCouncil && mine.applicants.length > 0 && (
            <Card title={`Bewerbungen (${mine.applicants.length})`}>
              <ul className="divide-y divide-stone-800 text-sm">
                {mine.applicants.map((a) => (
                  <li key={a.playerId} className="flex items-center gap-2 py-2">
                    <span className="flex-1">
                      {a.name}{' '}
                      <span className="text-xs text-stone-400">
                        {formatNumber(a.points)} Punkte
                      </span>
                    </span>
                    <Button
                      size="sm"
                      onClick={() =>
                        decide.mutate({ worldId, applicantId: a.playerId, accept: true })
                      }
                    >
                      Aufnehmen
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() =>
                        decide.mutate({ worldId, applicantId: a.playerId, accept: false })
                      }
                    >
                      Ablehnen
                    </Button>
                  </li>
                ))}
              </ul>
            </Card>
          )}

          <PlannerCard worldId={worldId} />
          {isDiplomat && <CircularForm worldId={worldId} />}
          <Card title="Diplomatie">
            {mine.relations.length === 0 && (
              <p className="text-sm text-stone-400">Noch keine anderen Stände in dieser Welt.</p>
            )}
            <ul className="divide-y divide-stone-800 text-sm">
              {mine.relations.map((r) => (
                <li key={r.alliance.id} className="flex flex-wrap items-center gap-2 py-2">
                  <span className="min-w-0 flex-1">
                    <span className="font-medium">
                      [{r.alliance.tag}] {r.alliance.name}
                    </span>
                    <span className="ml-2 text-xs text-stone-400">
                      {r.alliance.members} Mitglieder
                    </span>
                    <span className="block text-xs text-stone-500">
                      wir: {RELATION_LABELS[r.myStance]}
                      {r.myPending &&
                        ` (→ ${RELATION_LABELS[r.myPending.status]} ${formatAbsRel(r.myPending.effectiveFrom, now)})`}{' '}
                      · sie: {RELATION_LABELS[r.theirStance]}
                    </span>
                  </span>
                  <Badge tone={TONE[r.effective]}>{RELATION_LABELS[r.effective]}</Badge>
                  {isDiplomat && (
                    <select
                      className="rounded-md border border-stone-700 bg-stone-900 px-2 py-1 text-xs"
                      value={r.myPending?.status ?? r.myStance}
                      onChange={(e) =>
                        setStance.mutate({
                          worldId,
                          targetAllianceId: r.alliance.id,
                          stance: e.target.value as RelationStatus,
                        })
                      }
                    >
                      {RELATION_STATUS.map((s) => (
                        <option key={s} value={s}>
                          {RELATION_LABELS[s]}
                        </option>
                      ))}
                    </select>
                  )}
                </li>
              ))}
            </ul>
            <p className="mt-2 text-xs text-stone-500">
              Verbündet gilt nur, wenn beide Seiten es erklären. Feindlich gilt, sobald eine Seite
              es erklärt. Verschlechterungen greifen nach einer Abkühlzeit.
            </p>
          </Card>
        </>
      )}

      <Card title={`Alle Stände (${all.length})`}>
        {all.length === 0 && (
          <p className="text-sm text-stone-400">
            Noch kein Stand in dieser Welt. Gründe den ersten.
          </p>
        )}
        <ul className="divide-y divide-stone-800 text-sm">
          {all.map((a) => (
            <li key={a.id} className="flex flex-wrap items-center gap-2 py-2">
              <span className="min-w-0 flex-1">
                <span className="font-medium">
                  [{a.tag}] {a.name}
                </span>
                <span className="ml-2 text-xs text-stone-400">
                  {a.members} Mitglieder · {formatNumber(a.points)} Punkte
                </span>
                {a.description && (
                  <span className="block truncate text-xs text-stone-500">{a.description}</span>
                )}
              </span>
              {!membership && (
                <Button
                  size="sm"
                  onClick={() => apply.mutate({ worldId, allianceId: a.id })}
                  loading={apply.isPending}
                >
                  Bewerben
                </Button>
              )}
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}
